import React, { useEffect, useCallback, useState } from 'react'
import AppInput from '../../components/AppInput/AppInput'
import { useSelector, useDispatch } from 'react-redux';
import { Phone, User, CheckCircle, MapPin, Calendar, AlertTriangle, Plus } from 'lucide-react';
import AddClient from '../ClientManagement/AddClient';
import { HitApi, HitGraphQLApollo, HitGraphQLRaw } from '../../Api/ApiHit';
import { searchClient, updateSeat, addClient } from '../../constant/Constant';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppAutocomplete from '../../components/AppInput/AppAutocomplete';
import AppButton from '../../components/AppButton/AppButton';
import { getSelectedUnit } from '../../storage/Storage';
import { handleUpdateApollo } from '../../apollo/service';

function ClientDetailForm({ selectedSeat }) {

    console.log("selectedSeat", selectedSeat);
    const [laoding, setLoading] = useState(false);

    const ApiReducer = useSelector((state) => state.ApiReducer);

    const dispatch = useDispatch();
    const getUnit = getSelectedUnit()

    // State for managing autocomplete options
    const [clientOptions, setClientOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [lastSearchTerm, setLastSearchTerm] = useState('');

    // Clear confirmation state
    const [clearConfirm, setClearConfirm] = useState({ show: false, client: null });

    // New client creation state
    const [newClientName, setNewClientName] = useState('');
    const [isAddingClient, setIsAddingClient] = useState(false);

    // Confirm button loading state
    const [isConfirming, setIsConfirming] = useState(false);

    // Function to search clients via API
    const searchClients = useCallback(async (phoneNumber) => {
        if (!phoneNumber || phoneNumber.length <= 3) {
            setClientOptions([]);
            return;
        }

        // Avoid duplicate API calls for the same search term
        if (phoneNumber === lastSearchTerm) {
            return;
        }

        setIsLoading(true);
        setLastSearchTerm(phoneNumber);

        const json = {
            page: 1,
            limit: 20,
            search: {
                phoneNumber: phoneNumber
            }
        };

        try {
            const res = await HitApi(json, searchClient);
            console.log("res____", res);

            if (res?.success && res?.data) {
                // Format clients for autocomplete options
                const formattedOptions = res.data.map(client => ({
                    label: `${client.phoneNumber} - ${client.name}`,
                    value: client.phoneNumber,
                    clientData: client,
                    // Additional display information
                    subtitle: `Visits: ${client.totalVisit}${client.unpaidAmt > 0 ? ` | Due: ₹${client.unpaidAmt}` : ''}${client.ewalletAmt > 0 ? ` | Wallet: ₹${client.ewalletAmt}` : ''}`,
                    avatar: client.img,
                    name: client.name,
                    visits: client.totalVisit,
                    unpaidAmount: client.unpaidAmt || 0,
                    walletAmount: client.ewalletAmt || 0
                }));

                setClientOptions(formattedOptions);
            } else {
                setClientOptions([]);
            }
        } catch (error) {
            console.error("Error searching clients:", error);
            setClientOptions([]);
        } finally {
            setIsLoading(false);
        }
    }, [lastSearchTerm]);

    // Function to add new client
    const handleAddNewClient = async () => {
        if (!newClientName.trim() || ApiReducer?.apiJson?.phoneNumber?.length !== 10) {
            return;
        }

        setIsAddingClient(true);

        const json = {
            phoneNumber: ApiReducer?.apiJson?.phoneNumber,
            name: newClientName.trim(),
            unitIds: getUnit?._id
        };

        try {
            const res = await HitApi(json, addClient);
            console.log("Add client response:", res);

            if (res?.success) {
                // Update the form with the new client data
                const updatedApiJson = {
                    ...ApiReducer?.apiJson,
                    name: newClientName.trim(),
                    // Add other default fields as needed
                    gender: '',
                    email: '',
                };

                dispatch(setApiJson(updatedApiJson));

                // Set the new client as selected
                const newClient = {
                    _id: res?.data?._id || `new_${Date.now()}`,
                    phoneNumber: ApiReducer?.apiJson?.phoneNumber,
                    name: newClientName.trim(),
                    totalVisit: 0,
                    unpaidAmt: 0,
                    ewalletAmt: 0,
                    email: '',
                    gender: ''
                };

                setSelectedClient(newClient);
                setNewClientName('');

                console.log("New client added successfully:", newClient);
            } else {
                console.error("Failed to add client:", res?.message);
                // You might want to show an error message to the user here
            }
        } catch (error) {
            console.error("Error adding new client:", error);
            // You might want to show an error message to the user here
        } finally {
            setIsAddingClient(false);
        }
    };

    // Function to show clear confirmation
    const showClearConfirmation = (client) => {
        setClearConfirm({ show: true, client });
    };

    // Function to handle client clearing
    const handleClearClient = () => {
        setSelectedClient(null);
        setNewClientName('');
        const clearedApiJson = {
            ...ApiReducer?.apiJson,
            phoneNumber: '',
            name: '',
            gender: '',
            email: '',
        };
        dispatch(setApiJson(clearedApiJson));
        setClearConfirm({ show: false, client: null });
        console.log("Client selection cleared");
    };

    // Debounced search effect
    useEffect(() => {
        const phoneNumber = ApiReducer?.apiJson?.phoneNumber;

        if (phoneNumber && phoneNumber.length > 3) {
            // Add a small delay to avoid too many API calls while typing
            const timeoutId = setTimeout(() => {
                searchClients(phoneNumber);
            }, 500); // 500ms debounce

            return () => clearTimeout(timeoutId);
        } else {
            // Clear options if phone number is too short
            setClientOptions([]);
            setSelectedClient(null);
            setLastSearchTerm('');
            setNewClientName('');
        }
    }, [ApiReducer?.apiJson?.phoneNumber, searchClients]);

    // Handle client selection from autocomplete
    const handleClientSelect = (option) => {
        const client = option.clientData;

        // Update the form data with selected client information
        const updatedApiJson = {
            ...ApiReducer?.apiJson,
            phoneNumber: client.phoneNumber,
            name: client.name,
            gender: client.gender,
            email: client.email || '',
            // Add other fields as needed
        };

        dispatch(setApiJson(updatedApiJson));
        setSelectedClient(client);
        setNewClientName(''); // Clear new client name if existing client is selected

        console.log("Selected client:", client);
    };

    // Custom option renderer for rich client display
    const renderClientOption = (option, index, isHighlighted) => {
        const client = option.clientData;

        return (
            <div className={`px-4 py-3 cursor-pointer border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-blue-50' : 'hover:bg-slate-50'
                }`}>
                <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                        {client.img ? (
                            <img
                                src={client.img}
                                alt={client.name}
                                className="h-10 w-10 rounded-full object-cover"
                            />
                        ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-500" />
                            </div>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {client.phoneNumber} - {client.name}
                            </p>
                        </div>
                        <div className="flex items-center space-x-4 mt-1">
                            <span className="text-xs text-gray-500">
                                Visits: {client.totalVisit}
                            </span>
                            {client.unpaidAmt > 0 && (
                                <span className="text-xs text-red-500">
                                    Due: ₹{client.unpaidAmt}
                                </span>
                            )}
                            {client.ewalletAmt > 0 && (
                                <span className="text-xs text-green-500">
                                    Wallet: ₹{client.ewalletAmt}
                                </span>
                            )}
                        </div>
                    </div>
                    {selectedClient?._id === client._id && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                    )}
                </div>
            </div>
        );
    };

    // Updated handleConfirm function to use GraphQL mutation
    const handleConfirm = async () => {
        if (!selectedClient || !selectedSeat) {
            console.error("Missing selectedClient or selectedSeat");
            return;
        }
        setIsConfirming(true);
        var json = {
            _id: selectedSeat._id,
            seatCode: selectedSeat.seatCode || "default",
            seatFor: selectedSeat.seatFor || selectedSeat.seatName || "general",
            seatName: selectedSeat.seatName || "unnamed",
            clientInfo: [selectedClient._id],
            status: "Occupied",
            // unitId: getUnit?._id
        }
        const apolloRes = await handleUpdateApollo(json, setLoading);
        if(apolloRes?.updateSeat?.success){
            window.location.reload();
        }
    };

    return (
        <div>
            <div className="relative">
                <div className='mb-4'>Seat - {selectedSeat?.seatName} For {selectedSeat?.seatFor}</div>
                {/* Enhanced AppAutocomplete with custom rendering */}
                <div className="relative">
                    <AppAutocomplete
                        title="Contact Number"
                        placeholder="Enter contact number to search clients"
                        name="phoneNumber"
                        important={true}
                        error={ApiReducer?.apiJson?.phoneNumber?.length !== 10}
                        options={clientOptions}
                        optionLabel="label"
                        optionValue="value"
                        allowCustomValue={true}
                        onOptionSelect={handleClientSelect}
                        maxOptionsDisplay={10}
                        icon={<Phone className="h-4 w-4" />}
                        renderOption={renderClientOption}
                    />

                    {/* Loading indicator overlay */}
                    {isLoading && (
                        <div className="absolute right-12 top-1/2 transform -translate-y-1/2 z-20">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        </div>
                    )}
                </div>

                {/* Custom "No Results" message when no clients found */}
                {ApiReducer?.apiJson?.phoneNumber?.length === 10 &&
                    clientOptions.length === 0 &&
                    !isLoading &&
                    lastSearchTerm === ApiReducer?.apiJson?.phoneNumber && (
                        <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="text-center text-blue-700">
                                <User className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                                <p className="text-sm font-medium">No clients found</p>
                                <p className="text-xs mt-1 text-blue-600">
                                    Add a new client with phone number: {ApiReducer?.apiJson?.phoneNumber}
                                </p>

                                {/* New Client Form */}
                                <div className="mt-4 space-y-3">
                                    <div className="text-left">
                                        <AppInput
                                            title="Client Name"
                                            placeholder="Enter client name"
                                            value={newClientName}
                                            onChange={(e) => setNewClientName(e.target.value)}
                                            important={true}
                                            icon={<User className="h-4 w-4" />}
                                        />
                                    </div>

                                    <div className="flex space-x-2">
                                        <button
                                            onClick={handleAddNewClient}
                                            disabled={!newClientName.trim() || isAddingClient}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                        >
                                            {isAddingClient ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    <span>Adding...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Plus className="h-4 w-4" />
                                                    <span>Add New Client</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Show regular "No Results" for phone numbers < 10 digits */}
                {ApiReducer?.apiJson?.phoneNumber?.length > 3 &&
                    ApiReducer?.apiJson?.phoneNumber?.length < 10 &&
                    clientOptions.length === 0 &&
                    !isLoading &&
                    lastSearchTerm === ApiReducer?.apiJson?.phoneNumber && (
                        <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <div className="text-center text-gray-500">
                                <User className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm font-medium">No clients found</p>
                                <p className="text-xs mt-1 text-gray-400">
                                    Enter a complete 10-digit phone number to add a new client
                                </p>
                            </div>
                        </div>
                    )}

                {/* Selected client info */}
                {selectedClient && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-3">
                                        {selectedClient.img ? (
                                            <img
                                                src={selectedClient.img}
                                                alt={selectedClient.name}
                                                className="h-12 w-12 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="h-12 w-12 rounded-full bg-green-200 flex items-center justify-center">
                                                <User className="h-6 w-6 text-green-600" />
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-sm font-medium text-green-800">
                                                {selectedClient.name}
                                            </p>
                                            <p className="text-xs text-green-600">
                                                {selectedClient.phoneNumber}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Clear button for selected client */}
                                    <button
                                        onClick={() => showClearConfirmation(selectedClient)}
                                        className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded border border-gray-300 transition-colors duration-200"
                                        title="Clear selection"
                                    >
                                        Clear Selection
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                    <div className="flex items-center space-x-1">
                                        <Calendar className="h-3 w-3 text-green-600" />
                                        <span className="text-green-700">
                                            Visits: {selectedClient.totalVisit}
                                        </span>
                                    </div>

                                    {selectedClient.unpaidAmt > 0 && (
                                        <div className="flex items-center space-x-1">
                                            <span className="text-red-600 font-medium">
                                                Due: ₹{selectedClient.unpaidAmt}
                                            </span>
                                        </div>
                                    )}

                                    {selectedClient.ewalletAmt > 0 && (
                                        <div className="flex items-center space-x-1">
                                            <span className="text-green-600 font-medium">
                                                Wallet: ₹{selectedClient.ewalletAmt}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {selectedClient.email && (
                                    <p className="text-xs text-green-600 mt-1">
                                        Email: {selectedClient.email}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Clear Confirmation Modal */}
                {clearConfirm.show && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="flex-shrink-0">
                                    <AlertTriangle className="h-6 w-6 text-yellow-500" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900">
                                        Clear Client Selection
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        This will remove the selected client from the form.
                                    </p>
                                </div>
                            </div>

                            <div className="mb-6">
                                <p className="text-sm text-gray-700">
                                    Are you sure you want to clear the selection for <strong>{clearConfirm.client?.name}</strong>
                                    ({clearConfirm.client?.phoneNumber})?
                                </p>

                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                    <p className="text-xs text-blue-800">
                                        ℹ️ This will only clear the form selection. The client data will remain in your database.
                                    </p>
                                </div>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setClearConfirm({ show: false, client: null })}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleClearClient}
                                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Clear Selection
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Only show confirm button when client is selected */}
            {selectedClient && (
                <div className='mt-5 flex justify-end'>
                    <AppButton
                        buttontext={isConfirming ? "Confirming..." : "Confirm"}
                        onClick={handleConfirm}
                        disabled={isConfirming}
                    />
                </div>
            )}
        </div>
    )
}

export default ClientDetailForm